import { Search, Bell, Calendar as CalendarIcon, Menu, MapPin, Building, Megaphone, Plus } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { useState, useEffect, useRef } from 'react';
import NewLeadModal from './NewLeadModal';

export default function Topbar({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isSimulation = location.pathname === '/simulation';
  const isHomePage = location.pathname === '/';
  const [query, setQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showNewLeadModal, setShowNewLeadModal] = useState(false);
  const [dbResults, setDbResults] = useState([]);
  const [googleResults, setGoogleResults] = useState([]);
  const [googleError, setGoogleError] = useState(null);
  const [customersList, setCustomersList] = useState([]);
  const [newCustomersList, setNewCustomersList] = useState([]);
  const [newCustResults, setNewCustResults] = useState([]);
  const [leadsList, setLeadsList] = useState([]);
  const [leadsResults, setLeadsResults] = useState([]);
  const wrapperRef = useRef(null);

  const placesLibrary = useMapsLibrary('places');
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);

  // Initialize AutocompleteService and Session Token
  useEffect(() => {
    if (!placesLibrary) return;
    setAutocompleteService(new placesLibrary.AutocompleteService());
    setSessionToken(new placesLibrary.AutocompleteSessionToken());
  }, [placesLibrary]);

  // Fetch Marketing Leads
  useEffect(() => {
    if (!user) return;
    const url = `/api/leads?email=${user.google_email || ''}&role=${user.role || ''}&team_name=${user.team_name || ''}`;
    fetch(url)
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') {
          setLeadsList(data.data.filter(l => l.lead_status === 'New'));
        }
      })
      .catch(err => console.error("Error fetching leads in topbar:", err));
  }, [user]);

  // Fetch local customers — using the same filtering logic as the Dashboard map
  useEffect(() => {
    const isRestricted = user && user.role !== 'admin' && user.role !== 'super_admin' && user.team_name;

    const fetches = [
      fetch('/api/customers').then(res => res.json()),
      fetch(`/api/google_places?email=${user?.google_email || ''}`).then(res => res.json()),
    ];

    // Only fetch users + teams if we need to filter (non-admin)
    if (isRestricted) {
      fetches.push(
        fetch('/api/users').then(res => res.json()),
        fetch('/api/teams').then(res => res.json())
      );
    }

    Promise.all(fetches)
    .then(([custData, placesData, usersData, teamsData]) => {

      // Build salesCode → teamName map (same logic as Dashboard.jsx)
      let salesCodeToTeam = {};
      if (isRestricted && usersData && teamsData) {
        const teamIdToName = {};
        if (teamsData.status === 'success') {
          teamsData.data.forEach(t => { teamIdToName[t.id] = t.team_name; });
        }
        if (usersData.status === 'success') {
          usersData.data.forEach(u => {
            if (u.sales_code && u.team_id && teamIdToName[u.team_id]) {
              salesCodeToTeam[u.sales_code] = teamIdToName[u.team_id];
            }
          });
        }
      }

      if (custData.status === 'success') {
        let cData = custData.data;
        if (isRestricted) {
          // Mirror exact same logic as Dashboard.jsx lines 236-240
          cData = cData.filter(c => {
            const saleTeam      = salesCodeToTeam[c.update_sales_code];
            const afterSaleTeam = salesCodeToTeam[c.update_after_sales_code];
            return saleTeam === user.team_name || afterSaleTeam === user.team_name;
          });
        }
        setCustomersList(cData);
      }
      
      if (placesData.status === 'success') {
        let pData = placesData.data.filter(p => p.type === 'new_customer');
        if (isRestricted) {
          pData = pData.filter(p => p.saved_by_team === user.team_name);
        }
        setNewCustomersList(pData);
      }
    })
    .catch(err => console.error(err));
  }, [user]);

  // Handle outside click to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle search logic
  const handleSearch = (e) => {
    const val = e.target.value;
    setQuery(val);
    
    if (val.trim() === '') {
      setDbResults([]);
      setNewCustResults([]);
      setLeadsResults([]);
      setGoogleResults([]);
      setGoogleError(null);
      setIsDropdownOpen(false);
      return;
    }

    setIsDropdownOpen(true);

    // Search Database
    const lowerVal = val.toLowerCase();
    const filteredDb = customersList.filter(c => 
      (c.cd_no && c.cd_no.toLowerCase().includes(lowerVal)) ||
      (c.search_name && c.search_name.toLowerCase().includes(lowerVal)) ||
      (c.customer_name && c.customer_name.toLowerCase().includes(lowerVal))
    ).slice(0, 5); // Limit to 5 results
    setDbResults(filteredDb);
    
    // Search New Customers
    const filteredNewCust = newCustomersList.filter(c => 
      (c.name && c.name.toLowerCase().includes(lowerVal)) ||
      (c.address && c.address.toLowerCase().includes(lowerVal))
    ).slice(0, 5);
    setNewCustResults(filteredNewCust);

    // Search Marketing Leads
    const filteredLeads = leadsList.filter(l => 
      ((l.company_name_th || '').toLowerCase().includes(lowerVal)) ||
      ((l.company_name_en || '').toLowerCase().includes(lowerVal)) ||
      ((l.customer_name || '').toLowerCase().includes(lowerVal))
    ).slice(0, 5);
    setLeadsResults(filteredLeads);

    // Search Google Maps
    if (autocompleteService) {
      // Default bounds/bias for Thailand roughly
      const request = {
        input: val,
        componentRestrictions: { country: 'th' }, // restrict to Thailand if desired
        sessionToken: sessionToken
      };
      
      autocompleteService.getPlacePredictions(request, (predictions, status) => {
        if (predictions && predictions.length > 0) {
          setGoogleResults(predictions.slice(0, 5));
          setGoogleError(null);
        } else {
          setGoogleResults([]);
          if (status !== window.google?.maps?.places?.PlacesServiceStatus?.OK && status !== window.google?.maps?.places?.PlacesServiceStatus?.ZERO_RESULTS) {
             setGoogleError(status);
          } else {
             setGoogleError(null);
          }
        }
      });
    }
  };

  const handleSelectResult = (type, item) => {
    setIsDropdownOpen(false);
    setQuery('');
    navigate('/', { state: { searchResult: { type, data: item } } });
    
    // Reset session token after a selection is made
    if (placesLibrary) {
      setSessionToken(new placesLibrary.AutocompleteSessionToken());
    }
  };

  return (
    <header className="topbar">
      <div className="topbar-left" style={isSimulation ? { flex: 1, marginRight: '1.5rem', minWidth: 0 } : {}}>
        <div className="topbar-icon" style={{ display: 'none' /* For mobile menu toggle later */ }}>
          <Menu size={24} />
        </div>
        
        {isSimulation ? (
          <div id="topbar-portal-target" className="flex-1 w-full flex items-center min-w-0 overflow-hidden" style={{ gap: '0.75rem' }}></div>
        ) : (
        <div className="topbar-search" ref={wrapperRef} style={{ position: 'relative' }}>
          <Search size={18} color="#9ca3af" />
          <input 
            type="text" 
            placeholder="ค้นหาลูกค้า (CD No, ชื่อ) หรือ ค้นหาสถานที่บน Maps..." 
            value={query}
            onChange={handleSearch}
            onFocus={() => { if(query.trim() !== '') setIsDropdownOpen(true); }}
            style={{ width: '350px' }}
          />
          
          {/* Search Results Dropdown */}
          {isDropdownOpen && query.trim() !== '' && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              width: '100%',
              backgroundColor: 'white',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
              borderRadius: '8px',
              marginTop: '0.5rem',
              zIndex: 1000,
              maxHeight: '400px',
              overflowY: 'auto',
              border: '1px solid #e2e8f0'
            }}>
              
              {dbResults.length > 0 && (
                <div style={{ padding: '0.5rem 0' }}>
                  <div style={{ padding: '0.25rem 1rem', fontSize: '0.75rem', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    ฐานข้อมูลลูกค้าเรา
                  </div>
                  {dbResults.map((c, idx) => (
                    <div 
                      key={`db-${idx}`}
                      onClick={() => handleSelectResult('database', c)}
                      style={{ padding: '0.5rem 1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem', borderBottom: '1px solid #f1f5f9' }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <div style={{ backgroundColor: '#eff6ff', padding: '0.4rem', borderRadius: '6px' }}>
                        <Building size={16} color="#2563eb" />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1f2937' }}>{c.search_name}</span>
                        <span style={{ fontSize: '0.75rem', color: '#4b5563', marginTop: '0.1rem' }}>{c.customer_name || ''}</span>
                        <span style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.1rem' }}>CD No: {c.cd_no}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Marketing Leads Section */}
              {leadsResults.length > 0 && (
                <div style={{ padding: '0.5rem 0', borderTop: dbResults.length > 0 ? '1px solid #e2e8f0' : 'none' }}>
                  <div style={{ padding: '0.25rem 1rem', fontSize: '0.75rem', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Marketing Lead
                  </div>
                  {leadsResults.map((l, idx) => {
                    const displayName = l.company_name_th || l.company_name_en || l.customer_name || 'ไม่ระบุชื่อบริษัท';
                    return (
                      <div 
                        key={`lead-${idx}`}
                        onClick={() => handleSelectResult('lead', l)}
                        style={{ padding: '0.5rem 1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem', borderBottom: '1px solid #f1f5f9' }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <div style={{ backgroundColor: '#e0e7ff', padding: '0.4rem', borderRadius: '6px' }}>
                          <Building size={16} color="#4338ca" />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1f2937' }}>{displayName}</span>
                          <span style={{ fontSize: '0.7rem', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '280px' }}>{l.address_th || l.address_en || ''}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* New Customers Section */}
              {newCustResults.length > 0 && (
                <div style={{ padding: '0.5rem 0', borderTop: (dbResults.length > 0 || leadsResults.length > 0) ? '1px solid #e2e8f0' : 'none' }}>
                  <div style={{ padding: '0.25rem 1rem', fontSize: '0.75rem', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Sales Lead (ในทีม)
                  </div>
                  {newCustResults.map((c, idx) => (
                    <div 
                      key={`newc-${idx}`}
                      onClick={() => handleSelectResult('saved_place', c)}
                      style={{ padding: '0.5rem 1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem', borderBottom: '1px solid #f1f5f9' }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <div style={{ backgroundColor: '#ecfdf5', padding: '0.4rem', borderRadius: '6px' }}>
                        <Building size={16} color="#059669" />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1f2937' }}>{c.name}</span>
                        <span style={{ fontSize: '0.7rem', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '280px' }}>{c.address}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Google Maps Section Always Rendered */}
              <div style={{ padding: '0.5rem 0', borderTop: (dbResults.length > 0 || newCustResults.length > 0) ? '1px solid #e2e8f0' : 'none' }}>
                <div style={{ padding: '0.25rem 1rem', fontSize: '0.75rem', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Google Maps
                </div>
                
                {googleResults.length > 0 ? (
                  googleResults.map((place, idx) => (
                    <div 
                      key={`g-${idx}`}
                      onClick={() => handleSelectResult('google', place)}
                      style={{ padding: '0.5rem 1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem', borderBottom: '1px solid #f1f5f9' }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <div style={{ backgroundColor: '#fef3c7', padding: '0.4rem', borderRadius: '6px' }}>
                        <MapPin size={16} color="#d97706" />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1f2937' }}>{place.structured_formatting.main_text}</span>
                        <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>{place.structured_formatting.secondary_text}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '0.5rem 1rem', color: googleError ? '#ef4444' : '#6b7280', fontSize: '0.8rem' }}>
                    {!autocompleteService 
                      ? 'กำลังเชื่อมต่อ Google Maps...' 
                      : googleError 
                        ? `Google API Error: ${googleError}` 
                        : 'กำลังค้นหา หรือไม่พบสถานที่...'}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        )}
      </div>

      <div className="topbar-right" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        {isHomePage && (user?.role === 'super_admin' || user?.role === 'marketing' || user?.team_name?.toLowerCase() === 'marketing') && (
          <button
            type="button"
            onClick={() => setShowNewLeadModal(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.45rem',
              backgroundColor: '#eb0a1e',
              color: '#ffffff',
              border: 'none',
              padding: '0.45rem 0.9rem',
              borderRadius: '8px',
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(235, 10, 30, 0.25)',
              transition: 'all 0.15s ease-in-out',
              whiteSpace: 'nowrap'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#cc081a'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#eb0a1e'}
          >
            <Plus size={16} strokeWidth={2.5} />
            <Megaphone size={16} />
            <span>สร้าง Marketing Lead</span>
          </button>
        )}

        <div className="topbar-icon">
           {/* <CalendarIcon size={22} /> */} 
        </div>
        <div className="topbar-icon">
           {/* <Bell size={22} /> */} 
          <div className="notification-badge">12</div>
        </div>
        
        <div className="user-profile" onClick={() => {
          if(window.confirm('Do you want to log out?')) {
            onLogout();
          }
        }}>
          <div className="user-avatar">
            {user?.google_email?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="user-info">
            <span className="user-name">{user?.google_email?.split('@')[0]}</span>
            <span className="user-role">
              {user?.role === 'super_admin' ? 'Super Admin' : 
               user?.role === 'admin' ? 'Administrator' : 
               user?.role === 'sale_manager' ? 'Sales Manager' : 
               'Sales Executive'}
            </span>
          </div>
        </div>
      </div>

      {/* New Lead Modal */}
      <NewLeadModal
        isOpen={showNewLeadModal}
        onClose={() => setShowNewLeadModal(false)}
        locationData={null}
        user={user}
      />
    </header>
  );
}
